import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocale } from '@/i18n/useLocale';
import { useSession } from '@/stores/session';
import { paths } from '@/app/paths';
import { useActivityPing } from '@/socket/socket';
import { useInGameMark } from '@/stores/screen';
import { Disclaimer, cx } from '@/ui';
import type { GameId, GameSection } from '@tg/shared';
import { useGameMaterial } from '@/material/material';
import {
  AccountButton,
  DailyButton,
  DailyPlusButton,
  HeaderNav,
  LangSwitch,
  Logo,
  TabBar,
  WalletOrSignIn,
} from './parts';
import s from './shell.module.css';
import { UiIcon } from '@/art/UiIcon';
import { VersionLine } from '@/features/version/VersionLine';
import { JackpotChip } from '@/features/jackpot/JackpotChip';
import { markExitByButton } from '@/features/passport/tryMore';
import { QuestChip } from '@/features/quests/QuestCard';

/** Phone header of the lobby / system pages: logo · EN ▾ · balance · orange + (daily bonus). */
export function MobileHeader() {
  const isUser = useSession((st) => st.status === 'user');
  return (
    <div className={cx(s.mHeader, s.showMobile)}>
      <Logo />
      <LangSwitch variant="dropdown" />
      <WalletOrSignIn small />
      {isUser && <DailyPlusButton />}
    </div>
  );
}

/**
 * Right part of the desktop header: languages · balance · Daily bonus · avatar. Narrow desktop / tablet
 * (761–1100 px, `.hideNarrow` / `.showNarrow`): the languages become the «EN ▾» select and «Daily bonus» the
 * orange «+», as in the phone header — the header must never push the page sideways.
 */
function HeaderRight() {
  const isUser = useSession((st) => st.status === 'user');
  return (
    <div className={s.headerRight}>
      <LangSwitch compact className={s.hideNarrow} />
      <LangSwitch variant="dropdown" className={s.showNarrow} />
      <WalletOrSignIn />
      <DailyButton className={s.hideNarrow} />
      {isUser && <DailyPlusButton className={s.showNarrow} />}
      <AccountButton />
    </div>
  );
}

/** Footer link to the privacy notice (docs/owner-decisions.md 2026-09-30: in the footer of every page). */
export function PrivacyLink({ className }: { className?: string }) {
  const l = useLocale();
  return (
    <><Link to={paths.privacy(l)} className={cx(s.footerLink, className)}>
      <FormattedMessage id="privacy.title" />
    </Link>{' · '}<Link to={paths.cookies(l)} className={cx(s.footerLink, className)}><FormattedMessage id="cookies.link" /></Link></>
  );
}

/**
 * Shell for system pages (profile, fair, responsible, tournaments, friends, stubs).
 * Desktop: 56px header with nav. Phone: compact header + 4-tab bar. Footer: privacy links and build line;
 * the profile opts into the currency notice at the bottom (owner decision 2026-10-03).
 */
export function MainShell({ children, disclaimer = false }: { children: ReactNode; disclaimer?: boolean }) {
  return (
    <div className={s.frame}>
      <header className={cx(s.header, s.hideMobile)}>
        <Logo />
        <HeaderNav />
        <span className={s.grow} />
        <HeaderRight />
      </header>
      <MobileHeader />
      <main className={s.content}>{children}</main>
      <footer className={cx(s.footer, s.footerRow)}>
        {disclaimer ? <Disclaimer style={{ color: 'var(--muted)', fontSize: 12 }} /> : <PrivacyLink />}
        <VersionLine />
      </footer>
      <div className={s.showMobile} style={{ position: 'sticky', bottom: 0, flexDirection: 'column' }}>
        <TabBar />
      </div>
    </div>
  );
}

export interface GameShellProps {
  /** game title (proper names like "Liftoff" can be passed as plain text) */
  title: ReactNode;
  /** breadcrumb group */
  section?: GameSection;
  children: ReactNode;
  /** fill the viewport height, no page scroll (desktop game screens) */
  fixed?: boolean;
  /** send activity:ping for the daily time limit (default true; stubs pass false) */
  activity?: boolean;
  /** the game of the screen: switches on its material (src/material) when the game has one */
  game?: GameId;
}

/**
 * Shell for game screens (design crash/pot/roulette/slots):
 * desktop 56px header — logo · nav · "/ Originals / Liftoff" · languages · balance · Daily bonus · avatar;
 * phone 50px header — back · title · balance.
 * Currency/legal details live outside the playing field, in the profile footer.
 */
export function GameShell({
  title,
  section = 'originals',
  children,
  fixed,
  activity = true,
  game,
}: GameShellProps) {
  useActivityPing(activity);
  useInGameMark();
  const l = useLocale();
  const intl = useIntl();
  const material = useGameMaterial(game);
  // the game's accent under the header (desktop or phone, whichever is shown; material games only): a thin decorative strip, the header itself stays neutral
  const accent = material.on && (
    <div className={s.matAccent} aria-hidden="true" data-testid="material-accent" />
  );
  return (
    <div className={cx(s.frame, fixed && s.frameFixed)} {...material.attrs}>
      <header className={cx(s.header, s.gameHeader, s.hideMobile)}>
        <Logo />
        <HeaderNav />
        <div className={s.crumbs}>
          <span className={s.crumbSection}>
            / <FormattedMessage id={`common.section.${section}`} />
          </span>
          <span className={s.crumbTitle}>
            / <b>{title}</b>
          </span>
        </div>
        <span className={s.grow} />
        <QuestChip game={game} />
        <JackpotChip className={s.hideTablet} />
        <HeaderRight />
      </header>
      <div className={cx(s.mGameHeader, s.showMobile)}>
        <Link to={paths.lobby(l)} className={s.back} aria-label={intl.formatMessage({ id: 'common.back' })} onClick={markExitByButton}>
          <UiIcon name="back" size={20} fallback="‹" />
        </Link>
        <span className={s.mTitle}>{title}</span>
        <QuestChip game={game} small />
        <JackpotChip small className={s.mJackpot} />
        <WalletOrSignIn small />
      </div>
      {accent}
      <main className={s.content}>{children}</main>
    </div>
  );
}
